import { useSelector, useDispatch } from 'react-redux'
import { fetchPromotions, savePromotion, updatePromotion } from '../actions/promotions/promotions.actions'
import {
	redeemPromotionForFacilityVisit,
	deletePromotionForFacilityVisit
} from '../actions/promotions/redemptions.actions'
import { api } from '../services/api'
import { showError } from '../actions/misc/misc.actions'
import { resolveHouseholdId } from '../utils/promotions'

const usePromotions = () => {
	const dispatch = useDispatch()
	const promotions = useSelector((state) => state.specific.promotions.promotions)
	const selectedPromotion = useSelector((state) => state.specific.promotions.selected_promotion)
	const promosBusy = useSelector((state) => state.specific.promotions.busy)
	const redemptionError = useSelector((state) => state.specific.promotions.redemption_error)

	const getPromos = async ({ facilityId } = {}) => {
		try {
			await fetchPromotions(dispatch, { facilityId })
		} catch (e) {
			console.log(e)
		}
	}

	const savePromo = async (promo) => {
		try {
			await savePromotion(dispatch, promo)
		} catch (e) {
			console.log(e)
		}
	}

	const validatePromoForServiceTarget = async ({
		promo_code,
		household_id,
		visit_id = null,
		service_target,
		market_id = null,
		facility_id = null,
		training_course_abbreviation = null,
		training_course_start_date = null
	}) => {
		const payload = {
			valid: false,
			applicable_promo: null,
			redemption_error: '',
			validation_error: ''
		}
		try {
			const resolvedHouseholdId = resolveHouseholdId(household_id)
			const body = {
				service_target
			}

			if (visit_id) {
				body.visit_id = String(visit_id)
			}

			if (market_id) {
				body.market_id = market_id
			}

			if (facility_id) {
				body.facility_id = facility_id
			}

			if (training_course_abbreviation) {
				body.training_course_abbreviation = training_course_abbreviation
			}
			if (training_course_start_date) {
				body.training_course_start_date = training_course_start_date
			}

			const response = await api.post(
				`clients/${resolvedHouseholdId}/promo/validate/${promo_code}`,
				body
			)

			const { data, errors } = response.data
			if (data && data.length > 0) {
				payload.valid = true
				payload.applicable_promo = data[0]
			} else if (errors && errors.length > 0) {
				payload.validation_error = errors[0]
			}
		} catch (e) {
			dispatch(showError(e.message))
		} finally {
			return payload
		}
	}

	const validatePromoForFacilityVisit = async ({
		promo_code,
		household_id,
		visit_id,
		service_target,
		market_id = null,
		facility_id = null,
		training_course_abbreviation = null,
		training_course_start_date = null
	}) => {
		const payload = {
			valid: false,
			applicable_promo: null,
			validation_error: ''
		}
		try {
			const resolvedHouseholdId = resolveHouseholdId(household_id)
			const resolvedVisitId = visit_id != null ? String(visit_id) : null

			if (!resolvedVisitId) {
				payload.validation_error = 'Missing visit_id'
				return payload
			}

			const body = {
				service_target,
				visit_id: resolvedVisitId
			}

			if (market_id) {
				body.market_id = market_id
			}

			if (facility_id) {
				body.facility_id = facility_id
			}

			if (training_course_abbreviation) {
				body.training_course_abbreviation = training_course_abbreviation
			}
			if (training_course_start_date) {
				body.training_course_start_date = training_course_start_date
			}

			const response = await api.post(
				`clients/${resolvedHouseholdId}/promo/validate/${promo_code}`,
				body
			)

			const { data, errors } = response.data
			if (data && data.length > 0) {
				payload.valid = true
				payload.applicable_promo = {
					...data[0],
					service_target_promo_applied_to: service_target
				}
			} else if (errors && errors.length > 0) {
				payload.validation_error = errors[0]
			}
		} catch (e) {
			dispatch(showError(e.message))
		} finally {
			return payload
		}
	}

	const redeemPromoForFacilityVisit = async ({ promo_code, visit_id, household_id, service_target }) => {
		try {
			const redemptionStatus = await redeemPromotionForFacilityVisit(dispatch, {
				promo_code,
				visit_id,
				household_id,
				service_target
			})
			return redemptionStatus
		} catch (e) {
			console.log(e)
			return { success: false, redemption_error: 'Server Error' }
		}
	}

	const deletePromoForFacilityVisit = async ({ promo_code, promo_id, visit_id, household_id }) => {
		console.log(promo_code, promo_id, visit_id, household_id)
		try {
			const deleteStatus = await deletePromotionForFacilityVisit(dispatch, {
				promo_code,
				promo_id,
				visit_id,
				household_id
			})
			return deleteStatus
		} catch (e) {
			console.log(e)
			return { success: false, redemption_error: 'Server Error' }
		}
	}

	const updatePromo = async ({ updates = {}, promo_id }) => {
		try {
			await updatePromotion(dispatch, updates, promo_id)
		} catch (e) {
			console.log(e)
		}
	}

	const getExistingPromosInStateWithSameCode = (code) => {
		if (!promotions || !code) {
			return []
		}
		return promotions.filter((promo) => promo.code === code)
	}

	return {
		promotions,
		selectedPromotion,
		promosBusy,
		getPromos,
		getExistingPromosInStateWithSameCode,
		redeemPromoForFacilityVisit,
		savePromo,
		updatePromo,
		redemptionError,
		deletePromoForFacilityVisit,
		validatePromoForServiceTarget,
		validatePromoForFacilityVisit
	}
}

export default usePromotions
